import * as React from 'react';

import { getImageData } from 'lib/get-image-data';
import Image from 'lib/image-container';
import { useDetectPrint } from 'lib/use-detect-print';
import { MediaQueryWidth } from 'lib/use-media-query';
import useScrollbarWidth from 'lib/use-scrollbar-width';

import { Columns, ColumnsItem } from 'components/columns';
import { ColumnsSize } from 'components/columns/columns-item';
import { CtaBox, CtaBoxContent, CtaBoxHeader, CtaBoxText } from 'components/cta-box';
import PageSection from 'components/page/page-section';
import { Header2, Header3, P1 } from 'components/typography';

const PAGE_PADDING_SMALL = '16px';
const PAGE_PADDING_MEDIUM = '64px';
const PAGE_PADDING_LARGE = '108px';

type Props = {
  columnLeft?: {
    title?: string;
    subTitle?: string;
    subTitleElement?: React.ReactElement;
    content?: React.ReactNode;
    size?: ColumnsSize;
    align?: 'center' | 'start';
  };
  columnRight?: {
    title?: string;
    content?: React.ReactNode;
    size?: ColumnsSize;
    align?: 'center' | 'start';
  };
  image: {
    src: string;
    objectPosition: string;
    inlinePosition?: string;
    hasLoadPriority?: boolean;
  };
};

const PageSectionTwoColumnsImageFullwidth: React.FC<Props> = ({
  image,
  columnLeft,
  columnRight,
  // eslint-disable-next-line complexity
}) => {
  const scrollbarWidth = useScrollbarWidth(true);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const handleImageLoaded = () => setIsLoaded(true);
  const blurDataUrl = getImageData(image.src).blurDataUrl;
  const isPrinting = useDetectPrint();

  return (
    <PageSection padding="block" inner={true} extraClass="about-hero" color="transparent">
      <Columns
        // equal={true}
        hasMobileLayout={true}
        isMobileOrderInverted={image.inlinePosition !== 'start'}
        vAlignTop={true}
      >
        <ColumnsItem verticalAlign="stretch" size={columnLeft?.size || ColumnsSize['1/2']}>
          <CtaBox align={columnLeft?.align ? columnLeft?.align : 'start'}>
            {columnLeft?.title ? (
              <CtaBoxHeader>
                <Header2>{columnLeft.title}</Header2>
              </CtaBoxHeader>
            ) : null}
            {columnLeft?.subTitle ? (
              <CtaBoxText>
                <P1 html={columnLeft.subTitle} as="p" />
              </CtaBoxText>
            ) : null}

            {columnLeft?.content ? (
              <CtaBoxContent width="stretch">{columnLeft?.content}</CtaBoxContent>
            ) : null}
          </CtaBox>
        </ColumnsItem>

        <ColumnsItem size={columnRight?.size || ColumnsSize['1/2']}>
          <CtaBox align={columnRight?.align ? columnRight?.align : 'start'}>
            {columnRight?.title ? (
              <CtaBoxHeader>
                <Header3 as="p">{columnRight.title}</Header3>
              </CtaBoxHeader>
            ) : null}
            {columnRight?.content ? (
              <CtaBoxContent width="stretch">{columnRight?.content}</CtaBoxContent>
            ) : null}
          </CtaBox>
        </ColumnsItem>
      </Columns>

      <CtaBox align="center">
        <CtaBoxContent width="stretch">
          <div
            className="page-section__picture page-section__picture--border"
            style={
              blurDataUrl
                ? {
                    backgroundImage: `url("${blurDataUrl}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: image.objectPosition,
                  }
                : undefined
            }
          >
            {isPrinting ? null : (
              <Image
                priority={image.hasLoadPriority}
                width={1218}
                height={508}
                style={{
                  objectFit: 'cover',
                  objectPosition: image.objectPosition,
                  opacity: blurDataUrl && !isLoaded ? 0 : 1,
                  transition: 'opacity 0.5s',
                }}
                quality={75}
                src={image.src}
                alt=""
                onLoad={handleImageLoaded}
                sizes={`
                    (max-width: ${MediaQueryWidth['desktop-sm']}px) calc(100vw - 2 * ${PAGE_PADDING_SMALL} - ${scrollbarWidth}),
                    (max-width: ${MediaQueryWidth['tablet-lg']}px) calc(100vw - 2 * ${PAGE_PADDING_MEDIUM} - ${scrollbarWidth}),
                    (max-width: ${MediaQueryWidth['content-max']}px) calc(100vw - 2 * ${PAGE_PADDING_LARGE} - ${scrollbarWidth}),
                    1218px
                `}
              />
            )}
          </div>
        </CtaBoxContent>
      </CtaBox>
    </PageSection>
  );
};

export { PageSectionTwoColumnsImageFullwidth };
